import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import Select from 'react-select';
import axios from 'axios';

import { listCategories } from '../../orders/services/OrderCRUD';
import { listStores, Store } from '../../plans';
import {
  PROMO_TYPE_FIELDS,
  PROMO_TYPE_LABEL_IDS,
  PROMO_TYPES,
  ScheduledOfferDiscountMode,
  ScheduledOfferModel,
  ScheduledOfferPayload,
  ScheduledOfferPromoType,
  ScheduledOfferState,
} from '../models/ScheduledOfferModel';
import {
  createScheduledOffer,
  updateScheduledOffer,
} from '../services/ScheduledOfferCRUD';

const API_URL = process.env.REACT_APP_API_URL;

// Starting values of the type-specific fields, both for a new offer and for
// the reset that happens when the promo type stops using them.
const DEFAULT_BUY_QTY = 2;
const DEFAULT_FREE_QTY = 1;
const DEFAULT_NTH_UNIT = 2;
const DEFAULT_DISCOUNT_MODE: ScheduledOfferDiscountMode = 'relative';
const DEFAULT_DISCOUNT_VALUE = '50';

/** An early-access item being edited: its product is unset until picked. */
interface ItemRow {
  product: number | null;
  discount_value: string;
}

const EMPTY_ITEM_ROW: ItemRow = { product: null, discount_value: '' };

interface AdditionalProductVariant {
  id?: number;
  variant_id?: number;
  name?: string;
  variant_name?: string;
  ean?: string;
}

interface AdditionalProduct {
  variants: AdditionalProductVariant[];
}

interface SelectOption {
  value: number;
  label: string;
}

interface ScheduledOfferModalFormProps {
  scheduledOffer: ScheduledOfferModel | null;
  visible: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

/** Message ids of the field labels and hints some promo types reword. */
interface FieldMessageIds {
  product: string;
  buyQty: string;
  freeQty: string;
  quantitiesHint: string;
  maxUses: string;
  maxUsesHint: string;
}

const DEFAULT_FIELD_MESSAGE_IDS: FieldMessageIds = {
  product: 'scheduledOffers.product',
  buyQty: 'scheduledOffers.buyQty',
  freeQty: 'scheduledOffers.freeQty',
  quantitiesHint: 'scheduledOffers.quantitiesHint',
  maxUses: 'scheduledOffers.maxUsesPerUser',
  maxUsesHint: 'scheduledOffers.maxUsesHint',
};

const FIELD_MESSAGE_IDS_BY_TYPE: Partial<
  Record<ScheduledOfferPromoType, Partial<FieldMessageIds>>
> = {
  // Same fields as a multipack, but the product is the gift and the
  // quantities are the condition threshold and the gifted units.
  'gift-with-purchase': {
    product: 'scheduledOffers.giftProduct',
    buyQty: 'scheduledOffers.minUnits',
    freeQty: 'scheduledOffers.giftUnits',
    quantitiesHint: 'scheduledOffers.giftWithPurchaseQuantitiesHint',
    maxUses: 'scheduledOffers.maxGiftsPerUser',
    maxUsesHint: 'scheduledOffers.maxGiftsHint',
  },
};

/** Explanation shown under the type select, for the types that need one. */
const PROMO_TYPE_HINT_IDS: Partial<Record<ScheduledOfferPromoType, string>> = {
  'free-gift': 'scheduledOffers.giftHint',
  'gift-with-purchase': 'scheduledOffers.giftWithPurchaseHint',
  'free-shipping': 'scheduledOffers.freeShippingHint',
  'early-access': 'scheduledOffers.earlyAccessOfferHint',
};

function getVariantId(variant: AdditionalProductVariant): number | null {
  return variant.variant_id ?? variant.id ?? null;
}

function getVariantName(variant: AdditionalProductVariant): string {
  return variant.variant_name ?? variant.name ?? '';
}

function getVariantEan(variant: AdditionalProductVariant): string {
  return variant.ean ?? '';
}

function getVariantLabel(variant: AdditionalProductVariant): string {
  const name = getVariantName(variant);
  const ean = getVariantEan(variant);
  return ean ? `${name} - ${ean}` : name;
}

function toDatetimeLocal(isoDate: string | null): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

function toIsoString(localDatetime: string): string {
  if (!localDatetime) return '';
  return new Date(localDatetime).toISOString();
}

function nowDatetimeLocal(): string {
  return toDatetimeLocal(new Date().toISOString());
}

function todayStartMs(): number {
  const midnight = new Date();
  midnight.setHours(0, 0, 0, 0);
  return midnight.getTime();
}

function todayStartDatetimeLocal(): string {
  return toDatetimeLocal(new Date(todayStartMs()).toISOString());
}

/** Whether a stored date has already been reached; unset dates never have. */
function hasDatePassed(isoDate: string | null | undefined): boolean {
  if (!isoDate) return false;
  return new Date(isoDate).getTime() <= Date.now();
}

/**
 * Where the stored offer sits in time. Mirrors _validate_window() in
 * stores/models.py: a running offer has a frozen start, and a finished one
 * cannot be modified at all.
 */
type OfferPhase = 'new' | 'scheduled' | 'running' | 'finished';

function getOfferPhase(offer: ScheduledOfferModel | null): OfferPhase {
  if (!offer?.id) return 'new';
  const now = Date.now();
  if (new Date(offer.valid_until).getTime() < now) return 'finished';
  if (new Date(offer.valid_from).getTime() <= now) return 'running';
  return 'scheduled';
}

interface ProductVariantSelectProps {
  inputId: string;
  options: SelectOption[];
  value: number | null;
  onChange: (value: number | null) => void;
  placeholder: string;
  isDisabled?: boolean;
}

/** ProductVariant picker shared by the product, item and gift fields. */
function ProductVariantSelect({
  inputId,
  options,
  value,
  onChange,
  placeholder,
  isDisabled = false,
}: ProductVariantSelectProps) {
  return (
    <Select
      inputId={inputId}
      className="react-select-container"
      classNamePrefix="react-select"
      options={options}
      value={options.find((option) => option.value === value) || null}
      onChange={(option) => onChange(option ? option.value : null)}
      placeholder={placeholder}
      isDisabled={isDisabled}
    />
  );
}

interface ItemsEditorProps {
  rows: ItemRow[];
  options: SelectOption[];
  isDisabled: boolean;
  onChange: (rows: ItemRow[]) => void;
}

/** Products of an early-access offer, each with its own percentage off. */
function ItemsEditor({
  rows,
  options,
  isDisabled,
  onChange,
}: ItemsEditorProps) {
  const intl = useIntl();

  function updateRow(index: number, changes: Partial<ItemRow>) {
    onChange(
      rows.map((row, i) => (i === index ? { ...row, ...changes } : row))
    );
  }

  return (
    <div className="form-group">
      <label htmlFor="itemProduct0" className="form-label">
        {intl.formatMessage({ id: 'scheduledOffers.items' })}
      </label>
      <div className="d-flex flex-column gap-2">
        {rows.map((row, index) => (
          <div key={index} className="d-flex gap-2 align-items-center">
            {/* minWidth 0 lets a long product name be cut short instead of
                squeezing the discount input out of sight. */}
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <ProductVariantSelect
                inputId={`itemProduct${index}`}
                // A product can only be listed once: hide the other rows' ones.
                options={options.filter(
                  (option) =>
                    option.value === row.product ||
                    !rows.some((other) => other.product === option.value)
                )}
                value={row.product}
                onChange={(product) => updateRow(index, { product })}
                placeholder={intl.formatMessage({
                  id: 'scheduledOffers.product',
                })}
                isDisabled={isDisabled}
              />
            </div>
            <div className="input-group flex-shrink-0" style={{ width: 160 }}>
              <input
                type="number"
                className="form-control text-end"
                step="0.01"
                min={0.01}
                max={100}
                value={row.discount_value}
                placeholder={intl.formatMessage({
                  id: 'scheduledOffers.itemDiscount',
                })}
                disabled={isDisabled}
                onChange={(e) =>
                  updateRow(index, { discount_value: e.target.value })
                }
                required
              />
              <span className="input-group-text">%</span>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={isDisabled}
              onClick={() => onChange(rows.filter((_row, i) => i !== index))}
            >
              <span className="fa fa-trash" />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        className="btn btn-light-primary btn-sm mt-3"
        disabled={isDisabled}
        onClick={() => onChange([...rows, EMPTY_ITEM_ROW])}
      >
        <span className="fa fa-plus me-3" />
        {intl.formatMessage({ id: 'scheduledOffers.addItem' })}
      </button>
      <div className="form-text">
        {intl.formatMessage({ id: 'scheduledOffers.itemsHint' })}
      </div>
    </div>
  );
}

export default function ScheduledOfferModalForm({
  scheduledOffer,
  visible,
  onClose,
  onUpdated,
}: ScheduledOfferModalFormProps) {
  const intl = useIntl();
  const [productOptions, setProductOptions] = useState<SelectOption[]>([]);
  const [storeOptions, setStoreOptions] = useState<SelectOption[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<SelectOption[]>([]);
  const [promoType, setPromoType] = useState<ScheduledOfferPromoType>('price');
  const [name, setName] = useState<string>('');
  const [productId, setProductId] = useState<number | null>(null);
  const [storeId, setStoreId] = useState<number | null>(null);
  const [price, setPrice] = useState<string>('');
  const [buyQty, setBuyQty] = useState<number>(DEFAULT_BUY_QTY);
  const [freeQty, setFreeQty] = useState<number>(DEFAULT_FREE_QTY);
  const [nthUnit, setNthUnit] = useState<number>(DEFAULT_NTH_UNIT);
  const [discountMode, setDiscountMode] = useState<ScheduledOfferDiscountMode>(
    DEFAULT_DISCOUNT_MODE
  );
  const [discountValue, setDiscountValue] = useState<string>(
    DEFAULT_DISCOUNT_VALUE
  );
  const [validFrom, setValidFrom] = useState<string>('');
  const [validUntil, setValidUntil] = useState<string>('');
  const [earlyAccessFrom, setEarlyAccessFrom] = useState<string>('');
  const [earlyAccessUntil, setEarlyAccessUntil] = useState<string>('');
  const [earlyAccessGiftProductId, setEarlyAccessGiftProductId] = useState<
    number | null
  >(null);
  const [itemRows, setItemRows] = useState<ItemRow[]>([]);
  const [maxUsesPerUser, setMaxUsesPerUser] = useState<number>(1);
  const [conditionCategoryIds, setConditionCategoryIds] = useState<number[]>(
    []
  );
  const [conditionProductIds, setConditionProductIds] = useState<number[]>([]);
  const [state, setState] = useState<ScheduledOfferState>('active');
  const [dateError, setDateError] = useState<string>('');
  const [earlyAccessError, setEarlyAccessError] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  async function fetchProducts() {
    try {
      const { data } = await axios.get<AdditionalProduct[]>(
        `${API_URL}/api/additional-products/`
      );
      const options: SelectOption[] = [];
      data.forEach((product) => {
        const firstVariant = product.variants?.[0];
        if (!firstVariant) return;
        const id = getVariantId(firstVariant);
        if (id === null) return;
        options.push({ value: id, label: getVariantLabel(firstVariant) });
      });
      setProductOptions(options);
    } catch (error) {
      console.error('Error fetching additional products:', error);
    }
  }

  async function fetchStores() {
    try {
      const { data } = await listStores();
      setStoreOptions(
        data.map((store: Store) => ({ value: store.id, label: store.name }))
      );
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  }

  async function fetchCategories() {
    try {
      const { data } = await listCategories();
      setCategoryOptions(
        data.map((category) => ({ value: category.id, label: category.name }))
      );
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }

  function resetForm() {
    if (scheduledOffer?.id) {
      setPromoType(scheduledOffer.promo_type ?? 'price');
      setName(scheduledOffer.name);
      setProductId(scheduledOffer.product);
      setStoreId(scheduledOffer.store);
      setPrice(scheduledOffer.price ?? '');
      setBuyQty(scheduledOffer.buy_qty ?? DEFAULT_BUY_QTY);
      setFreeQty(scheduledOffer.free_qty ?? DEFAULT_FREE_QTY);
      setNthUnit(scheduledOffer.nth_unit ?? DEFAULT_NTH_UNIT);
      setDiscountMode(scheduledOffer.discount_mode ?? DEFAULT_DISCOUNT_MODE);
      setDiscountValue(scheduledOffer.discount_value ?? DEFAULT_DISCOUNT_VALUE);
      setValidFrom(toDatetimeLocal(scheduledOffer.valid_from));
      setValidUntil(toDatetimeLocal(scheduledOffer.valid_until));
      setEarlyAccessFrom(toDatetimeLocal(scheduledOffer.early_access_from));
      setEarlyAccessUntil(toDatetimeLocal(scheduledOffer.early_access_until));
      setEarlyAccessGiftProductId(
        scheduledOffer.early_access_gift_product ?? null
      );
      setItemRows(scheduledOffer.items ?? []);
      setMaxUsesPerUser(scheduledOffer.max_uses_per_user ?? 1);
      setConditionCategoryIds(scheduledOffer.condition_categories ?? []);
      setConditionProductIds(scheduledOffer.condition_products ?? []);
      setState(scheduledOffer.state);
    } else {
      setPromoType('price');
      setName('');
      setProductId(null);
      setStoreId(null);
      setPrice('');
      setBuyQty(DEFAULT_BUY_QTY);
      setFreeQty(DEFAULT_FREE_QTY);
      setNthUnit(DEFAULT_NTH_UNIT);
      setDiscountMode(DEFAULT_DISCOUNT_MODE);
      setDiscountValue(DEFAULT_DISCOUNT_VALUE);
      setValidFrom('');
      setValidUntil('');
      setEarlyAccessFrom('');
      setEarlyAccessUntil('');
      setEarlyAccessGiftProductId(null);
      setItemRows([]);
      setMaxUsesPerUser(1);
      setConditionCategoryIds([]);
      setConditionProductIds([]);
      setState('active');
    }
    setDateError('');
    setEarlyAccessError('');
  }

  function onPromoTypeChange(nextType: ScheduledOfferPromoType) {
    setPromoType(nextType);
    // Clearing is required, not cosmetic: the backend rejects a payload that
    // carries a field belonging to another promo type.
    const nextFields = PROMO_TYPE_FIELDS[nextType];
    if (!nextFields.product) setProductId(null);
    if (!nextFields.price) setPrice('');
    if (!nextFields.quantities) {
      setBuyQty(DEFAULT_BUY_QTY);
      setFreeQty(DEFAULT_FREE_QTY);
    }
    if (!nextFields.nthUnit) setNthUnit(DEFAULT_NTH_UNIT);
    if (!nextFields.discount) {
      setDiscountMode(DEFAULT_DISCOUNT_MODE);
      setDiscountValue(DEFAULT_DISCOUNT_VALUE);
    }
    // Only early access lists items, so it always starts from one empty row.
    setItemRows(nextFields.items ? [EMPTY_ITEM_ROW] : []);
    if (!nextFields.earlyAccess) {
      setEarlyAccessFrom('');
      setEarlyAccessUntil('');
      setEarlyAccessGiftProductId(null);
      setEarlyAccessError('');
    }
    if (!nextFields.conditionProducts) {
      setConditionCategoryIds([]);
      setConditionProductIds([]);
    }
  }

  function buildPayload(): ScheduledOfferPayload {
    const activeFields = PROMO_TYPE_FIELDS[promoType];
    return {
      promo_type: promoType,
      name,
      store: storeId as number,
      // Anything the type does not use travels as null.
      product: activeFields.product ? productId : null,
      price: activeFields.price ? price : null,
      buy_qty: activeFields.quantities ? buyQty : null,
      free_qty: activeFields.quantities ? freeQty : null,
      nth_unit: activeFields.nthUnit ? nthUnit : null,
      discount_mode: activeFields.discount ? discountMode : null,
      discount_value: activeFields.discount ? discountValue : null,
      // isFormValid guarantees every row has a product by now.
      items: activeFields.items
        ? itemRows.map(({ product, discount_value }) => ({
            product: product as number,
            discount_value,
          }))
        : [],
      early_access_from: activeFields.earlyAccess
        ? toIsoString(earlyAccessFrom)
        : null,
      early_access_until: activeFields.earlyAccess
        ? toIsoString(earlyAccessUntil)
        : null,
      early_access_gift_product: activeFields.earlyAccess
        ? earlyAccessGiftProductId
        : null,
      valid_from: toIsoString(validFrom),
      valid_until: toIsoString(validUntil),
      max_uses_per_user: maxUsesPerUser,
      condition_categories: activeFields.conditionProducts
        ? conditionCategoryIds
        : [],
      condition_products: activeFields.conditionProducts
        ? conditionProductIds
        : [],
      state,
    };
  }

  function extractErrorMessage(error: unknown): string {
    const fallback = intl.formatMessage({ id: 'scheduledOffers.error' });
    const data = (error as { response?: { data?: unknown } })?.response?.data;
    if (!data) return fallback;
    if (typeof data === 'string') return data;
    if (Array.isArray(data)) return data.join(', ');
    if (typeof data === 'object') {
      const messages: string[] = [];
      Object.entries(data as Record<string, unknown>).forEach(
        ([key, value]) => {
          if (Array.isArray(value)) {
            messages.push(`${key}: ${value.join(', ')}`);
          } else if (typeof value === 'string') {
            messages.push(`${key}: ${value}`);
          }
        }
      );
      return messages.length ? messages.join(' | ') : fallback;
    }
    return fallback;
  }

  /**
   * Mirrors _validate_early_access() in stores/models.py: the window must be
   * ordered and sit inside the offer's own [validFrom, validUntil].
   */
  function getEarlyAccessWindowError(): string {
    if (!PROMO_TYPE_FIELDS[promoType].earlyAccess) return '';
    const from = new Date(earlyAccessFrom).getTime();
    const until = new Date(earlyAccessUntil).getTime();
    if (from >= until) {
      return intl.formatMessage({
        id: 'scheduledOffers.earlyAccessFromAfterUntil',
      });
    }
    if (
      from < new Date(validFrom).getTime() ||
      until > new Date(validUntil).getTime()
    ) {
      return intl.formatMessage({
        id: 'scheduledOffers.earlyAccessOutsideWindow',
      });
    }
    return '';
  }

  async function onSubmit() {
    if (!validFrom || !validUntil) return;
    setEarlyAccessError('');
    if (isFinished) {
      setDateError(
        intl.formatMessage({ id: 'scheduledOffers.finishedNotEditable' })
      );
      return;
    }
    if (new Date(validFrom) >= new Date(validUntil)) {
      setDateError(
        intl.formatMessage({ id: 'scheduledOffers.validFromAfterUntil' })
      );
      return;
    }
    // The end must always stay in the future; the start is only validated while
    // it is still editable (a running offer has it frozen).
    if (new Date(validUntil).getTime() <= Date.now()) {
      setDateError(
        intl.formatMessage({ id: 'scheduledOffers.validUntilInPast' })
      );
      return;
    }
    if (!isStartLocked && new Date(validFrom).getTime() < todayStartMs()) {
      setDateError(
        intl.formatMessage({ id: 'scheduledOffers.validFromInPast' })
      );
      return;
    }
    setDateError('');
    const windowError = getEarlyAccessWindowError();
    if (windowError) {
      setEarlyAccessError(windowError);
      return;
    }
    setSubmitting(true);
    try {
      if (scheduledOffer?.id) {
        await updateScheduledOffer(scheduledOffer.id, buildPayload());
        toast.success(
          intl.formatMessage({ id: 'scheduledOffers.updateSuccess' })
        );
      } else {
        await createScheduledOffer(buildPayload());
        toast.success(
          intl.formatMessage({ id: 'scheduledOffers.createSuccess' })
        );
      }
      onUpdated();
    } catch (error) {
      toast.error(extractErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(() => {
    fetchProducts();
    fetchStores();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!visible) return;
    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduledOffer, visible]);

  const fields = PROMO_TYPE_FIELDS[promoType];
  const messageIds: FieldMessageIds = {
    ...DEFAULT_FIELD_MESSAGE_IDS,
    ...FIELD_MESSAGE_IDS_BY_TYPE[promoType],
  };
  const promoTypeHintId = PROMO_TYPE_HINT_IDS[promoType];
  // A type hidden from PROMO_TYPES must still show while editing an offer that
  // uses it, or the select renders blank and saving would silently retype it.
  const promoTypeOptions = PROMO_TYPES.includes(promoType)
    ? PROMO_TYPES
    : [...PROMO_TYPES, promoType];
  const phase = getOfferPhase(scheduledOffer);
  const isFinished = phase === 'finished';
  const isStartLocked = phase === 'running' || isFinished;
  // Same rule as validFrom: once the early access has begun, who qualifies for
  // it (plan active before its start) must not change under the customers.
  const hasEarlyAccessStarted =
    phase === 'running' && hasDatePassed(scheduledOffer?.early_access_from);
  const isEarlyAccessStartLocked = hasEarlyAccessStarted || isFinished;
  const priceNumber = Number(price);
  const isPriceValid =
    !fields.price ||
    (price !== '' && !Number.isNaN(priceNumber) && priceNumber > 0);
  const areQuantitiesValid =
    !fields.quantities ||
    (Number.isInteger(buyQty) &&
      buyQty >= 1 &&
      Number.isInteger(freeQty) &&
      freeQty >= 1);
  const isProductValid = !fields.product || productId !== null;
  // The union of categories and products counts, so either one is enough.
  const isConditionValid =
    !fields.conditionProducts ||
    conditionCategoryIds.length > 0 ||
    conditionProductIds.length > 0;
  const isNthUnitValid =
    !fields.nthUnit || (Number.isInteger(nthUnit) && nthUnit >= 2);
  const discountNumber = Number(discountValue);
  const isDiscountValid =
    !fields.discount ||
    (discountValue !== '' &&
      !Number.isNaN(discountNumber) &&
      discountNumber > 0 &&
      (discountMode === 'absolute' || discountNumber <= 100));
  const itemProductIds = itemRows.map((row) => row.product);
  // Mirrors _validate_items() in the backend serializer.
  const areItemsValid =
    !fields.items ||
    (itemRows.length > 0 &&
      itemRows.every(
        (row) =>
          row.product !== null &&
          Number(row.discount_value) > 0 &&
          Number(row.discount_value) <= 100
      ) &&
      new Set(itemProductIds).size === itemProductIds.length);
  const isEarlyAccessValid =
    !fields.earlyAccess ||
    (earlyAccessFrom !== '' &&
      earlyAccessUntil !== '' &&
      earlyAccessGiftProductId !== null);
  const isFormValid =
    name.trim() !== '' &&
    isProductValid &&
    isConditionValid &&
    storeId !== null &&
    isPriceValid &&
    areQuantitiesValid &&
    isNthUnitValid &&
    isDiscountValid &&
    areItemsValid &&
    isEarlyAccessValid &&
    validFrom !== '' &&
    validUntil !== '' &&
    Number.isInteger(maxUsesPerUser) &&
    maxUsesPerUser >= 1;

  return (
    <Modal
      show={visible}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {scheduledOffer?.id
            ? intl.formatMessage({ id: 'scheduledOffers.edit' })
            : intl.formatMessage({ id: 'scheduledOffers.add' })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column gap-4">
          {isFinished && (
            <div className="alert alert-warning mb-0">
              {intl.formatMessage({
                id: 'scheduledOffers.finishedNotEditable',
              })}
            </div>
          )}
          <div className="form-group">
            <label htmlFor="promoType" className="form-label">
              {intl.formatMessage({ id: 'scheduledOffers.promoType' })}
            </label>
            <select
              id="promoType"
              className="form-select"
              value={promoType}
              onChange={(e) =>
                onPromoTypeChange(e.target.value as ScheduledOfferPromoType)
              }
            >
              {promoTypeOptions.map((type) => (
                <option key={type} value={type}>
                  {intl.formatMessage({ id: PROMO_TYPE_LABEL_IDS[type] })}
                </option>
              ))}
            </select>
            {promoTypeHintId && (
              <div className="form-text">
                {intl.formatMessage({ id: promoTypeHintId })}
              </div>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="name" className="form-label">
              {intl.formatMessage({ id: 'scheduledOffers.name' })}
            </label>
            <input
              type="text"
              className="form-control"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          {fields.conditionProducts && (
            <>
              <div className="form-group">
                <label htmlFor="conditionCategory" className="form-label">
                  {intl.formatMessage({
                    id: 'scheduledOffers.conditionCategory',
                  })}
                </label>
                <Select
                  inputId="conditionCategory"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isMulti
                  options={categoryOptions}
                  value={categoryOptions.filter((option) =>
                    conditionCategoryIds.includes(option.value)
                  )}
                  onChange={(options) =>
                    setConditionCategoryIds(
                      options.map((option) => option.value)
                    )
                  }
                  placeholder={intl.formatMessage({
                    id: 'scheduledOffers.conditionCategory',
                  })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="conditionProducts" className="form-label">
                  {intl.formatMessage({
                    id: 'scheduledOffers.conditionProducts',
                  })}
                </label>
                <Select
                  inputId="conditionProducts"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isMulti
                  options={productOptions}
                  value={productOptions.filter((option) =>
                    conditionProductIds.includes(option.value)
                  )}
                  onChange={(options) =>
                    setConditionProductIds(
                      options.map((option) => option.value)
                    )
                  }
                  placeholder={intl.formatMessage({
                    id: 'scheduledOffers.conditionProducts',
                  })}
                />
                <div className="form-text">
                  {intl.formatMessage({ id: 'scheduledOffers.conditionHint' })}
                </div>
              </div>
            </>
          )}
          {fields.product && (
            <div className="form-group">
              <label htmlFor="product" className="form-label">
                {intl.formatMessage({ id: messageIds.product })}
              </label>
              <ProductVariantSelect
                inputId="product"
                options={productOptions}
                value={productId}
                onChange={setProductId}
                placeholder={intl.formatMessage({ id: messageIds.product })}
              />
            </div>
          )}
          {fields.items && (
            <ItemsEditor
              rows={itemRows}
              options={productOptions}
              isDisabled={isFinished}
              onChange={setItemRows}
            />
          )}
          <div className="form-group">
            <label htmlFor="store" className="form-label">
              {intl.formatMessage({ id: 'scheduledOffers.store' })}
            </label>
            <Select
              inputId="store"
              className="react-select-container"
              classNamePrefix="react-select"
              options={storeOptions}
              value={
                storeOptions.find((option) => option.value === storeId) || null
              }
              onChange={(option) => setStoreId(option ? option.value : null)}
              placeholder={intl.formatMessage({
                id: 'scheduledOffers.store',
              })}
            />
          </div>
          {fields.price && (
            <div className="form-group">
              <label htmlFor="price" className="form-label">
                {intl.formatMessage({ id: 'scheduledOffers.price' })}
              </label>
              <input
                type="number"
                className="form-control"
                step="0.01"
                min={0}
                id="price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
              />
            </div>
          )}
          {(fields.nthUnit || fields.discount) && (
            // align-items-end: the labels are not the same length, so without
            // it a wrapping one pushes its input below the others.
            <div className="row g-4 align-items-end">
              {fields.nthUnit && (
                <div className="col-4">
                  <label htmlFor="nthUnit" className="form-label">
                    {intl.formatMessage({ id: 'scheduledOffers.nthUnit' })}
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    step="1"
                    min={2}
                    id="nthUnit"
                    value={nthUnit}
                    onChange={(e) =>
                      setNthUnit(Math.max(2, parseInt(e.target.value, 10) || 2))
                    }
                    required
                  />
                </div>
              )}
              <div className={fields.nthUnit ? 'col-4' : 'col-6'}>
                <label htmlFor="discountMode" className="form-label">
                  {intl.formatMessage({ id: 'scheduledOffers.discountMode' })}
                </label>
                <select
                  id="discountMode"
                  className="form-select"
                  value={discountMode}
                  onChange={(e) =>
                    setDiscountMode(
                      e.target.value as ScheduledOfferDiscountMode
                    )
                  }
                >
                  <option value="relative">
                    {intl.formatMessage({
                      id: 'scheduledOffers.discountMode.relative',
                    })}
                  </option>
                  <option value="absolute">
                    {intl.formatMessage({
                      id: 'scheduledOffers.discountMode.absolute',
                    })}
                  </option>
                </select>
              </div>
              <div className={fields.nthUnit ? 'col-4' : 'col-6'}>
                <label htmlFor="discountValue" className="form-label">
                  {intl.formatMessage({ id: 'scheduledOffers.discountValue' })}
                </label>
                <input
                  type="number"
                  className="form-control"
                  step="0.01"
                  min={0}
                  max={discountMode === 'relative' ? 100 : undefined}
                  id="discountValue"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  required
                />
              </div>
              <div className="col-12">
                <div className="form-text">
                  {intl.formatMessage({
                    id: fields.nthUnit
                      ? 'scheduledOffers.nthUnitHint'
                      : 'scheduledOffers.productDiscountHint',
                  })}
                </div>
              </div>
            </div>
          )}
          {fields.quantities && (
            <div className="row g-4 align-items-end">
              <div className="col-6">
                <label htmlFor="buyQty" className="form-label">
                  {intl.formatMessage({ id: messageIds.buyQty })}
                </label>
                <input
                  type="number"
                  className="form-control"
                  step="1"
                  min={1}
                  id="buyQty"
                  value={buyQty}
                  onChange={(e) =>
                    setBuyQty(Math.max(1, parseInt(e.target.value, 10) || 1))
                  }
                  required
                />
              </div>
              <div className="col-6">
                <label htmlFor="freeQty" className="form-label">
                  {intl.formatMessage({ id: messageIds.freeQty })}
                </label>
                <input
                  type="number"
                  className="form-control"
                  step="1"
                  min={1}
                  id="freeQty"
                  value={freeQty}
                  onChange={(e) =>
                    setFreeQty(Math.max(1, parseInt(e.target.value, 10) || 1))
                  }
                  required
                />
              </div>
              <div className="col-12">
                <div className="form-text">
                  {intl.formatMessage({ id: messageIds.quantitiesHint })}
                </div>
              </div>
            </div>
          )}
          <div className="form-group">
            <label htmlFor="validFrom" className="form-label">
              {intl.formatMessage({ id: 'scheduledOffers.validFrom' })}
            </label>
            <input
              type="datetime-local"
              className="form-control"
              id="validFrom"
              value={validFrom}
              min={isStartLocked ? undefined : todayStartDatetimeLocal()}
              disabled={isStartLocked}
              onChange={(e) => setValidFrom(e.target.value)}
              required
            />
            {phase === 'running' && (
              <div className="form-text">
                {intl.formatMessage({ id: 'scheduledOffers.startLockedHint' })}
              </div>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="validUntil" className="form-label">
              {intl.formatMessage({ id: 'scheduledOffers.validUntil' })}
            </label>
            <input
              type="datetime-local"
              className="form-control"
              id="validUntil"
              value={validUntil}
              min={nowDatetimeLocal()}
              disabled={isFinished}
              onChange={(e) => setValidUntil(e.target.value)}
              required
            />
            {dateError && <div className="text-danger mt-1">{dateError}</div>}
          </div>
          {fields.earlyAccess && (
            <div className="row g-4 align-items-end">
              <div className="col-6">
                <label htmlFor="earlyAccessFrom" className="form-label">
                  {intl.formatMessage({
                    id: 'scheduledOffers.earlyAccessFrom',
                  })}
                </label>
                <input
                  type="datetime-local"
                  className="form-control"
                  id="earlyAccessFrom"
                  value={earlyAccessFrom}
                  min={validFrom || undefined}
                  max={validUntil || undefined}
                  disabled={isEarlyAccessStartLocked}
                  onChange={(e) => setEarlyAccessFrom(e.target.value)}
                  required
                />
              </div>
              <div className="col-6">
                <label htmlFor="earlyAccessUntil" className="form-label">
                  {intl.formatMessage({
                    id: 'scheduledOffers.earlyAccessUntil',
                  })}
                </label>
                <input
                  type="datetime-local"
                  className="form-control"
                  id="earlyAccessUntil"
                  value={earlyAccessUntil}
                  min={earlyAccessFrom || validFrom || undefined}
                  max={validUntil || undefined}
                  disabled={isFinished}
                  onChange={(e) => setEarlyAccessUntil(e.target.value)}
                  required
                />
              </div>
              {hasEarlyAccessStarted && (
                <div className="col-12">
                  <div className="form-text">
                    {intl.formatMessage({
                      id: 'scheduledOffers.earlyAccessStartLockedHint',
                    })}
                  </div>
                </div>
              )}
              {earlyAccessError && (
                <div className="col-12">
                  <div className="text-danger">{earlyAccessError}</div>
                </div>
              )}
              <div className="col-12">
                <label htmlFor="earlyAccessGiftProduct" className="form-label">
                  {intl.formatMessage({
                    id: 'scheduledOffers.earlyAccessGiftProduct',
                  })}
                </label>
                <ProductVariantSelect
                  inputId="earlyAccessGiftProduct"
                  options={productOptions}
                  value={earlyAccessGiftProductId}
                  onChange={setEarlyAccessGiftProductId}
                  placeholder={intl.formatMessage({
                    id: 'scheduledOffers.giftProduct',
                  })}
                  isDisabled={isFinished}
                />
                <div className="form-text">
                  {intl.formatMessage({
                    id: 'scheduledOffers.earlyAccessHint',
                  })}
                </div>
              </div>
            </div>
          )}
          {fields.maxUses && (
            <div className="form-group">
              <label htmlFor="maxUsesPerUser" className="form-label">
                {intl.formatMessage({ id: messageIds.maxUses })}
              </label>
              <input
                type="number"
                className="form-control"
                step="1"
                min={1}
                id="maxUsesPerUser"
                value={maxUsesPerUser}
                onChange={(e) =>
                  setMaxUsesPerUser(
                    Math.max(1, parseInt(e.target.value, 10) || 1)
                  )
                }
                required
              />
              <div className="form-text">
                {intl.formatMessage({ id: messageIds.maxUsesHint })}
              </div>
            </div>
          )}
          <div className="form-group">
            <label htmlFor="state" className="form-label">
              {intl.formatMessage({ id: 'scheduledOffers.state' })}
            </label>
            <select
              id="state"
              className="form-select"
              value={state}
              onChange={(e) => setState(e.target.value as ScheduledOfferState)}
            >
              <option value="active">
                {intl.formatMessage({ id: 'scheduledOffers.active' })}
              </option>
              <option value="inactive">
                {intl.formatMessage({ id: 'scheduledOffers.inactive' })}
              </option>
            </select>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onClose}>
          {intl.formatMessage({ id: 'common.cancel' })}
        </button>
        <button
          onClick={onSubmit}
          className="btn btn-primary"
          disabled={!isFormValid || submitting || isFinished}
        >
          {scheduledOffer?.id
            ? intl.formatMessage({ id: 'common.save' })
            : intl.formatMessage({ id: 'common.create' })}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
